/**
 * SFrame-artige E2EE-Schicht (Welle 7c) — opt-in.
 *
 * Verschlüsselt jeden Medien-Frame zusätzlich zur Transport-DTLS-SRTP-Schicht
 * mit AES-256-GCM, mit einem Schlüssel der NUR zwischen den beiden Endpunkten
 * existiert (ECDH-Key-Agreement, Server sieht ihn nie). Damit ist garantiertes
 * AES-256-GCM erreicht, unabhängig davon was Chromium für SRTP aushandelt.
 *
 * Dies ist eine pragmatische SFrame-Variante (kein vollständiger IETF-SFrame-
 * Header), ausreichend für P2P mit einem gemeinsamen Schlüssel. Aktiviert nur
 * wenn beide Seiten Insertable Streams unterstützen UND das Feature-Flag gesetzt
 * ist. Bei fehlender Unterstützung: sauberer Fallback auf reines DTLS-SRTP.
 *
 * Browser-Unterstützung: Chromium via RTCRtpScriptTransform ODER
 * createEncodedStreams(). Firefox/Safari werden zur Laufzeit erkannt.
 */

import { debugEnabled } from '@/lib/debug';

export interface SframeContext {
  key: CryptoKey;
  /** kurze Key-ID fürs Framing (1 Byte), erlaubt späteres Re-Keying */
  keyId: number;
}

const IV_LENGTH = 12; // AES-GCM Standard-IV

/** ECDH-Schlüsselpaar erzeugen (P-256). */
export async function generateEcdhKeyPair(): Promise<CryptoKeyPair> {
  return crypto.subtle.generateKey({ name: 'ECDH', namedCurve: 'P-256' }, false, ['deriveKey']);
}

/** Eigenen Public Key als Base64 (raw) exportieren — geht über Signaling an den Peer. */
export async function exportPublicKey(pair: CryptoKeyPair): Promise<string> {
  const raw = await crypto.subtle.exportKey('raw', pair.publicKey);
  return btoa(String.fromCharCode(...new Uint8Array(raw)));
}

/** Aus eigenem Private Key + fremdem Public Key den gemeinsamen AES-256-GCM-Key ableiten. */
export async function deriveSharedKey(pair: CryptoKeyPair, peerPublicB64: string): Promise<CryptoKey> {
  const peerRaw = Uint8Array.from(atob(peerPublicB64), (c) => c.charCodeAt(0));
  const peerKey = await crypto.subtle.importKey(
    'raw', peerRaw, { name: 'ECDH', namedCurve: 'P-256' }, false, [],
  );
  return crypto.subtle.deriveKey(
    { name: 'ECDH', public: peerKey },
    pair.privateKey,
    { name: 'AES-GCM', length: 256 },
    false,
    ['encrypt', 'decrypt'],
  );
}

/** Prüft ob der Browser Insertable Streams unterstützt (eine der beiden APIs). */
export function sframeSupported(): boolean {
  const w = window as any;
  const hasScriptTransform = typeof w.RTCRtpScriptTransform !== 'undefined';
  const sender = w.RTCRtpSender?.prototype;
  const hasEncodedStreams = sender && typeof sender.createEncodedStreams === 'function';
  return Boolean(hasScriptTransform || hasEncodedStreams);
}

/** Bevorzugte API: moderne RTCRtpScriptTransform (Worker) wenn verfügbar — sie läuft
 *  off-main-thread und erlaubt saubere Keyframes via generateKeyFrame().
 *  Override: ?sframe=legacy (oder localStorage bamoa_sframe=legacy) erzwingt den
 *  Legacy-Pfad (createEncodedStreams) — nützlich zum A/B-Testen am Gerät. */
export function workerTransformSupported(): boolean {
  try {
    const q = new URL(window.location.href).searchParams.get('sframe');
    if (q === 'legacy' || localStorage.getItem('bamoa_sframe') === 'legacy') return false;
  } catch { /* ignore */ }
  return typeof (window as any).RTCRtpScriptTransform !== 'undefined';
}

// ── Welle 8c-pre23: SFrame-Worker (lazy) ──────────────────────────────────────
let sframeWorker: Worker | null = null;
let workerBroken = false;
let lastWorkerStats: { enc: number; decOk: number; decFail: number; decPass: number } | null = null;
function getSframeWorker(): Worker | null {
  if (workerBroken) return null;
  if (!sframeWorker && workerTransformSupported()) {
    try {
      sframeWorker = new Worker(new URL('./sframe.worker.ts', import.meta.url), { type: 'module' });
      sframeWorker.onerror = (e) => { console.warn('[sframe] Worker-Fehler', e); };
      sframeWorker.onmessage = (e) => {
        if (e.data?.type === 'stats') lastWorkerStats = e.data; // Welle 8c-pre25: Diagnose
      };
    } catch (e) {
      console.warn('[sframe] Worker konnte nicht erstellt werden → Legacy-Pfad', e);
      workerBroken = true;
      sframeWorker = null;
    }
  }
  return sframeWorker;
}

/** Welle 8c-pre25: Krypto-Zähler für die Diagnose. Liefert die Worker-Zähler, wenn der
 *  Worker-Pfad läuft, sonst die Main-Thread-(Legacy-)Zähler. */
export function getCryptoStats(): { enc: number; decOk: number; decFail: number; decPass: number } {
  if (lastWorkerStats) return lastWorkerStats;
  return { enc: encCount, decOk, decFail, decPass };
}

/** Schlüssel/Status einer Session an den Worker melden (no-op ohne Worker). */
function workerPostKey(sessionId: string, key: CryptoKey, keyId: number) {
  getSframeWorker()?.postMessage({ type: 'key', sessionId, key, keyId });
}
function workerPostEncryptReady(sessionId: string) {
  getSframeWorker()?.postMessage({ type: 'encrypt-ready', sessionId });
}
function workerPostMode(sessionId: string, on: boolean) {
  getSframeWorker()?.postMessage({ type: 'mode', sessionId, on });
}

/** Sauberen Keyframe auf allen Video-Sendern erzwingen (Worker-Pfad, generateKeyFrame).
 *  Der Aufrufer entscheidet anhand der Sender, ob der Worker-Pfad aktiv ist. */
export function requestEncoderKeyframe(): void {
  getSframeWorker()?.postMessage({ type: 'keyframe' });
}

// Magic-Marker, um UNSERE verschlüsselten Frames zweifelsfrei zu erkennen.
// So werden unverschlüsselte Übergangs-Frames nicht fälschlich „entschlüsselt".
const MAGIC0 = 0xba;
const MAGIC1 = 0x5f;
const HDR = 3; // magic(2) + keyId(1)

// Diagnose-Zähler (Welle 7e4+). Logging nur im einheitlichen Debug-Modus.
let encCount = 0, decOk = 0, decFail = 0, decPass = 0;
let firstErrs = 0;
let lastStatsAt = 0;
function maybeLogStats() {
  if (!debugEnabled()) return;
  const now = Date.now();
  if (now - lastStatsAt > 3000) {
    lastStatsAt = now;
    console.log(`[dbg][sframe] enc=${encCount} decOk=${decOk} decFail=${decFail} passthrough=${decPass}`);
  }
}

/** Frame verschlüsseln: [magic(2)|keyId(1)|iv(12)|ciphertext]. */
async function encryptFrame(ctx: SframeContext, data: ArrayBuffer): Promise<ArrayBuffer> {
  const iv = crypto.getRandomValues(new Uint8Array(IV_LENGTH));
  const cipher = await crypto.subtle.encrypt({ name: 'AES-GCM', iv }, ctx.key, data);
  const out = new Uint8Array(HDR + IV_LENGTH + cipher.byteLength);
  out[0] = MAGIC0; out[1] = MAGIC1; out[2] = ctx.keyId & 0xff;
  out.set(iv, HDR);
  out.set(new Uint8Array(cipher), HDR + IV_LENGTH);
  encCount++;
  return out.buffer;
}

/** Frame entschlüsseln. Rückgabe:
 *  - ArrayBuffer: Klartext (kein Magic → Übergangs-Frame, unverändert durchreichen)
 *    bzw. erfolgreich entschlüsselt.
 *  - null: Magic vorhanden, aber Entschlüsselung fehlgeschlagen (falscher/fehlender
 *    Schlüssel) → Frame FALLENLASSEN. Müll an den Decoder zu geben würde dessen
 *    Referenz dauerhaft zerstören (Artefakte, die nie weggehen) und unterdrückt die
 *    native Keyframe-Anforderung. Fallenlassen lässt den Decoder „stehen", was der
 *    Freeze-Watchdog erkennt und per Keyframe-Anforderung sauber repariert. */
async function decryptFrame(ctx: SframeContext, data: ArrayBuffer): Promise<ArrayBuffer | null> {
  const buf = new Uint8Array(data);
  if (buf.length < HDR + IV_LENGTH || buf[0] !== MAGIC0 || buf[1] !== MAGIC1) {
    decPass++;
    return data; // nicht von uns verschlüsselt → unverändert (Übergangs-Klartext)
  }
  try {
    const iv = buf.slice(HDR, HDR + IV_LENGTH);
    const cipher = buf.slice(HDR + IV_LENGTH);
    const out = await crypto.subtle.decrypt({ name: 'AES-GCM', iv }, ctx.key, cipher);
    decOk++;
    return out;
  } catch (e) {
    decFail++;
    if (firstErrs < 3) { firstErrs++; console.warn('[sframe] decrypt fail → Frame verworfen', e); }
    return null; // fallenlassen statt Müll dekodieren
  }
}

/** Transform-Stream mit mutable Context-Getter — reicht Frames durch bis der
 *  Key etabliert ist, danach wird ver-/entschlüsselt. */
function makeTransform(getCtx: () => SframeContext | null, mode: 'encrypt' | 'decrypt'): TransformStream {
  return new TransformStream({
    async transform(frame: any, controller: any) {
      const ctx = getCtx();
      if (!ctx) { controller.enqueue(frame); return; } // Key noch nicht da → durchreichen
      if (mode === 'encrypt') {
        try { frame.data = await encryptFrame(ctx, frame.data); } catch { /* Frame unverändert lassen */ }
        controller.enqueue(frame);
      } else {
        let out: ArrayBuffer | null;
        try { out = await decryptFrame(ctx, frame.data); }
        catch { controller.enqueue(frame); maybeLogStats(); return; }
        if (out === null) { maybeLogStats(); return; } // undekodierbar → fallenlassen (kein Müll an Decoder)
        frame.data = out;
        controller.enqueue(frame);
      }
      maybeLogStats();
    },
  });
}

/**
 * SFrame an einen RTCRtpSender/Receiver anhängen. Bevorzugt den Worker-Transform
 * (off-main-thread, saubere Keyframes); fällt sonst auf Legacy createEncodedStreams
 * zurück. Gibt true zurück wenn verdrahtet.
 */
export function applySframe(
  rtpObj: RTCRtpSender | RTCRtpReceiver,
  session: E2eeSession,
  mode: 'encrypt' | 'decrypt',
): boolean {
  const anyObj = rtpObj as any;
  const w = window as any;
  const kind = anyObj.track?.kind ?? 'video';
  try {
    // Bevorzugt: Worker-Transform (RTCRtpScriptTransform).
    if (workerTransformSupported()) {
      const worker = getSframeWorker();
      if (worker) {
        anyObj.transform = new w.RTCRtpScriptTransform(worker, { operation: mode, sessionId: session.id, kind });
        console.log(`[sframe] Pfad: ${mode}/${kind} → WORKER (RTCRtpScriptTransform)`);
        return true;
      }
    }
    // Fallback: Legacy createEncodedStreams (Main-Thread). Wire-Format identisch.
    if (typeof anyObj.createEncodedStreams === 'function') {
      const getCtx = mode === 'encrypt' ? session.getEncryptCtx : session.getDecryptCtx;
      const streams = anyObj.createEncodedStreams();
      streams.readable.pipeThrough(makeTransform(getCtx, mode)).pipeTo(streams.writable);
      console.log(`[sframe] Pfad: ${mode}/${kind} → LEGACY (createEncodedStreams)`);
      return true;
    }
  } catch (e) {
    console.warn('[sframe] applySframe fehlgeschlagen', e);
  }
  return false;
}

/** Test-Override für den E2EE-Intent: ?e2ee=1 erzwingt an, ?e2ee=0 erzwingt aus.
 *  null = kein Override → der server-autoritative Wert (Tenant-Policy) gilt.
 *  Für Tests bitte auf BEIDEN Seiten gleich setzen. */
export function e2eeForced(): boolean | null {
  try {
    const url = new URL(window.location.href);
    const q = url.searchParams.get('e2ee');
    if (q === '1') return true;
    if (q === '0') return false;
    if (localStorage.getItem('bamoa_e2ee') === '1') return true;
    return null;
  } catch { return null; }
}

/**
 * E2EE-Key-Session mit getrennten Engagement-Stufen für „nie scheitern":
 *  - DECRYPT wird aktiv, sobald der gemeinsame Schlüssel abgeleitet ist (harmlos —
 *    dank Magic-Marker werden nur echte verschlüsselte Frames angefasst).
 *  - ENCRYPT wird erst aktiv, wenn der Peer bestätigt hat, dass ER den Schlüssel
 *    hat (rtc:e2ee-ready). Damit verschlüsselt eine Seite NIE, bevor die andere
 *    entschlüsseln kann → keine schwarzen Frames, Medien fließen immer.
 *  Kommt der Handshake nicht zustande, bleibt es bei Klartext (Passthrough) →
 *  die Verbindung scheitert nie, Verschlüsselung ist rein additiv.
 */
export interface E2eeSession {
  /** eindeutige Session-ID (z.B. 'av' oder 'companion-screen') — Worker-Schlüssel-Mapping */
  id: string;
  keyId: number;
  exportPublicKey: () => Promise<string>;
  onPeerKey: (publicKeyB64: string) => Promise<void>;
  markPeerReady: () => void;
  hasKey: () => boolean;
  /** Welle 7e: dynamischer Modus — false pausiert die Verschlüsselung nahtlos
   *  (z.B. wenn ein 3. Teilnehmer dazukommt). Entschlüsselung bleibt immer bereit. */
  setMode: (on: boolean) => void;
  getEncryptCtx: () => SframeContext | null;
  getDecryptCtx: () => SframeContext | null;
  isActive: () => boolean;     // verschlüsselt gerade tatsächlich
  isSuspended: () => boolean;  // bereit, aber pausiert (z.B. Mehrparteien)
}

export async function createE2eeSession(id = 'av'): Promise<E2eeSession> {
  const keyId = 1;
  const pair = await generateEcdhKeyPair();
  let ctx: SframeContext | null = null;
  let peerReady = false;
  let modeOn = true; // Default an; wird bei >2 Teilnehmern auf false gesetzt
  const ready = () => ctx !== null && peerReady;
  return {
    id,
    keyId,
    exportPublicKey: () => exportPublicKey(pair),
    async onPeerKey(peerPublicB64: string) {
      try {
        const key = await deriveSharedKey(pair, peerPublicB64);
        ctx = { key, keyId };
        workerPostKey(id, key, keyId); // Worker-Pfad: Schlüssel in den Worker
        console.log('[sframe] Shared Key abgeleitet — Entschlüsselung bereit');
      } catch (e) {
        console.warn('[sframe] Key-Ableitung fehlgeschlagen', e);
      }
    },
    markPeerReady() {
      peerReady = true;
      workerPostEncryptReady(id); // Worker-Pfad: ab jetzt darf verschlüsselt werden
      if (ctx && modeOn) console.log('[sframe] Peer bereit — Verschlüsselung aktiv (beidseitig E2EE)');
    },
    setMode(on: boolean) {
      if (modeOn === on) return;
      modeOn = on;
      workerPostMode(id, on); // Worker-Pfad: Modus dynamisch
      console.log(`[sframe] E2EE-Modus → ${on ? 'AKTIV' : 'PAUSIERT (z.B. Mehrparteien)'}`);
    },
    hasKey: () => ctx !== null,
    getDecryptCtx: () => ctx,
    getEncryptCtx: () => (ready() && modeOn ? ctx : null),
    isActive: () => ready() && modeOn,
    isSuspended: () => ready() && !modeOn,
  };
}
